import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY, formatDate, todayISO } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const ACCOUNTS = [
  "現金", "普通預金", "売掛金", "仮払消費税",
  "売上高", "仕入高", "通信費", "交通費", "消耗品費", "支払手数料",
  "地代家賃", "水道光熱費", "雑費", "資本金", "利益剰余金",
];

const newLine = () => ({ account: "現金", debit: 0, credit: 0 });

export default function JournalEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    date: todayISO(),
    description: "",
    lines: [
      { account: "現金", debit: 0, credit: 0 },
      { account: "売上高", debit: 0, credit: 0 },
    ],
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal-entries");
      setEntries(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.round(totalDebit * 100) === Math.round(totalCredit * 100);

  const updateLine = (idx, key, value) => {
    const next = [...form.lines];
    next[idx] = { ...next[idx], [key]: value };
    setForm({ ...form, lines: next });
  };

  const handleCreate = async () => {
    if (!balanced) {
      toast.error("借方と貸方の合計が一致しません");
      return;
    }
    if (!form.description) {
      toast.error("摘要を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.map((l) => ({
          account: l.account,
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
        })),
      };
      await api.post("/journal-entries", payload);
      toast.success("仕訳を登録しました");
      setOpen(false);
      setForm({
        date: todayISO(),
        description: "",
        lines: [newLine(), newLine()],
      });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!confirm("この仕訳を削除しますか？")) return;
    try {
      await api.delete(`/journal-entries/${id}`);
      toast.success("削除しました");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="journal-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">仕訳</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">仕訳帳</h1>
          <p className="mt-1 text-sm text-stone-500">複式簿記に基づいた取引記録</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="journal-create-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950"
            >
              <Plus className="h-4 w-4" /> 仕訳を入力
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display">仕訳の入力</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">日付</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="journal-date" />
                </div>
                <div>
                  <Label className="text-sm">摘要</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="例: 売上計上" className="mt-1.5" data-testid="journal-description" />
                </div>
              </div>

              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <div className="bg-stone-50/70 px-3 py-2 text-xs uppercase tracking-wider font-semibold text-stone-500 grid grid-cols-12 gap-2">
                  <div className="col-span-5">勘定科目</div>
                  <div className="col-span-3 text-right">借方</div>
                  <div className="col-span-3 text-right">貸方</div>
                  <div className="col-span-1"></div>
                </div>
                {form.lines.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 p-2 border-t border-stone-100 items-center" data-testid={`journal-line-${idx}`}>
                    <select
                      value={l.account}
                      onChange={(e) => updateLine(idx, "account", e.target.value)}
                      className="col-span-5 h-10 rounded-md border border-stone-300 bg-white px-2 text-sm"
                      data-testid={`journal-line-account-${idx}`}
                    >
                      {ACCOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <Input type="number" className="col-span-3 text-right" value={l.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} data-testid={`journal-line-debit-${idx}`} />
                    <Input type="number" className="col-span-3 text-right" value={l.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} data-testid={`journal-line-credit-${idx}`} />
                    <button
                      type="button"
                      disabled={form.lines.length <= 2}
                      onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}
                      className="col-span-1 text-stone-400 hover:text-orange-600 disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4 mx-auto" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, lines: [...form.lines, newLine()] })}
                  className="w-full px-4 py-2 text-sm text-emerald-800 hover:bg-emerald-50 border-t border-stone-200 inline-flex items-center justify-center gap-1"
                  data-testid="journal-add-line"
                >
                  <Plus className="h-3.5 w-3.5" /> 行を追加
                </button>
              </div>

              <div className={`rounded-md border px-3 py-2 text-sm ${balanced ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-orange-200 bg-orange-50 text-orange-700"}`} data-testid="journal-balance-indicator">
                借方: <span className="font-numbers">{formatJPY(totalDebit)}</span> / 貸方: <span className="font-numbers">{formatJPY(totalCredit)}</span> — {balanced ? "✓ 一致" : "× 不一致"}
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">キャンセル</button>
              <button
                onClick={handleCreate}
                disabled={submitting || !balanced}
                data-testid="journal-submit-button"
                className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} 登録する
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-900" /></div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-stone-500">
            <BookOpen className="h-10 w-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
            仕訳が登録されていません
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {entries.map((entry) => (
              <div key={entry.id} className="p-5" data-testid={`journal-entry-${entry.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-medium text-stone-900">{entry.description}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{formatDate(entry.date)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-stone-500">合計</div>
                      <div className="font-numbers font-medium text-stone-900">{formatJPY(entry.total)}</div>
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="h-8 w-8 rounded-md hover:bg-orange-50 inline-flex items-center justify-center text-stone-400 hover:text-orange-600"
                      data-testid={`journal-delete-${entry.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50/70 text-xs uppercase tracking-wider text-stone-500">
                        <th className="px-3 py-2 text-left font-semibold">勘定科目</th>
                        <th className="px-3 py-2 text-right font-semibold">借方</th>
                        <th className="px-3 py-2 text-right font-semibold">貸方</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.lines.map((l, idx) => (
                        <tr key={idx} className="border-t border-stone-100">
                          <td className="px-3 py-2 text-stone-700">{l.account}</td>
                          <td className="px-3 py-2 text-right font-numbers text-stone-900">{l.debit ? formatJPY(l.debit) : "—"}</td>
                          <td className="px-3 py-2 text-right font-numbers text-stone-900">{l.credit ? formatJPY(l.credit) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
